import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./AdminDashboard.css";
import Navbar from "./Navbar";
import Footer from "./Footer";

const AdminDashboard = () => {
  const [abstracts, setAbstracts] = useState([]);
  const [papers, setPapers] = useState([]);
  const [users, setUsers] = useState([]);
  const [remarks, setRemarks] = useState({});
  const [paperReviews, setPaperReviews] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("abstracts"); // "abstracts", "papers", or "users"
  const [showMasterLogin, setShowMasterLogin] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [masterPassword, setMasterPassword] = useState('');
  const [isImpersonating, setIsImpersonating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch abstracts
        const abstractsResponse = await axios.get("https://api.rescons.in/get-all-abstracts", {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("adminToken")}` }
        });

        const allAbstracts = [];
        (abstractsResponse.data.abstracts || []).forEach(entry => {
          const { uid, fullName, email, abstractSubmissions = [] } = entry;
          abstractSubmissions.forEach(abs => {
            if (abs.abstractCode) {
              allAbstracts.push({ uid, fullName, email, ...abs });
            }
          });
        });
        setAbstracts(allAbstracts);

        // Fetch papers
        const papersResponse = await axios.get("https://api.rescons.in/api/papers/admin/all", {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("adminToken")}` }
        });
        setPapers(papersResponse.data.papers || []);

        // Fetch users
        const usersResponse = await axios.get("https://api.rescons.in/admin/all-users", {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("adminToken")}` }
        });
        setUsers(usersResponse.data.users || []);

      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Error fetching data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const updateStatus = async (uid, abstractCode, status) => {
    try {
      const remarkText = remarks[abstractCode] || "";
      await axios.put("https://api.rescons.in/admin/update-abstract-status", {
        uid,
        abstractCode,
        status,
        remarks: remarkText
      }, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("adminToken")}` }
      });

      setAbstracts(prev =>
        prev.map(abs =>
          abs.abstractCode === abstractCode ? { ...abs, status } : abs
        )
      );
    } catch (error) {
      setError("Error updating abstract status");
    }
  };

  const updatePaperReview = async (paperCode) => {
    try {
      const reviewData = paperReviews[paperCode];
      if (!reviewData) return;

      await axios.put(`https://api.rescons.in/api/papers/admin/${paperCode}/review`, {
        reviewerComments: reviewData.reviewerComments,
        technicalScore: reviewData.technicalScore,
        presentationScore: reviewData.presentationScore,
        overallScore: reviewData.overallScore,
        recommendation: reviewData.recommendation,
        reviewerName: reviewData.reviewerName || "Admin"
      }, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("adminToken")}` }
      });

             // Update local state
      setPapers(prev =>
        prev.map(paper =>
          paper.paperCode === paperCode 
            ? { 
                ...paper, 
                status: (() => {
                  const rec = reviewData.recommendation;
                  if (!rec) return paper.status;
                  if (rec === 'Accept without change') return 'Accepted';
                  if (
                    rec === 'Accept with suggested but not mandatory changes' ||
                    rec === 'Accepted subject to mandatory changes' ||
                    rec === 'Wish to see revised version'
                  ) return 'Revision Required';
                  return paper.status;
                })(),
                review: {
                   reviewerComments: reviewData.reviewerComments || '',
                   technicalScore: reviewData.technicalScore || null,
                   presentationScore: reviewData.presentationScore || null,
                   overallScore: reviewData.overallScore || null,
                  recommendation: reviewData.recommendation || '',
                   reviewerName: reviewData.reviewerName || "Admin",
                   reviewDate: new Date()
                 }
               }
             : paper
         )
       );

      // Clear the review form
      setPaperReviews(prev => {
        const newReviews = { ...prev };
        delete newReviews[paperCode];
        return newReviews;
      });

    } catch (error) {
      console.error("Error updating paper review:", error);
      setError("Error updating paper review");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminToken");
    navigate("/admin-login");
  };

  const handleMasterLogin = (user) => {
    setSelectedUser(user);
    setShowMasterLogin(true);
  };

  const handleMasterPasswordSubmit = () => {
    // Master password - you can change this to whatever you want
    const MASTER_PASSWORD = "admin123"; // Change this to your desired master password
    
    if (masterPassword === MASTER_PASSWORD) {
      // Store original admin token
      const originalAdminToken = sessionStorage.getItem("adminToken");
      sessionStorage.setItem("originalAdminToken", originalAdminToken);
      
      // Create a fake user token for impersonation
      const impersonationToken = btoa(JSON.stringify({
        id: selectedUser.uid,
        uid: selectedUser.uid,
        email: selectedUser.email,
        fullName: selectedUser.fullName,
        isImpersonated: true,
        originalAdmin: true
      }));
      
      // Store impersonation data
      sessionStorage.setItem("token", impersonationToken);
      sessionStorage.setItem("uid", selectedUser.uid);
      sessionStorage.setItem("impersonatedUser", JSON.stringify(selectedUser));
      
      setIsImpersonating(true);
      setShowMasterLogin(false);
      setMasterPassword('');
      
      // Navigate to user dashboard
      navigate('/');
      
    } else {
      alert('Incorrect master password!');
    }
  };

  const exitImpersonation = () => {
    // Restore original admin session
    const originalAdminToken = sessionStorage.getItem("originalAdminToken");
    if (originalAdminToken) {
      sessionStorage.setItem("adminToken", originalAdminToken);
      sessionStorage.removeItem("originalAdminToken");
    }
    
    // Clear impersonation data
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("uid");
    sessionStorage.removeItem("impersonatedUser");
    
    setIsImpersonating(false);
    navigate("/admin-dashboard");
  };

  if (loading) return <p className="loading">Loading...</p>;
  if (error) return <p className="error-message">{error}</p>;

  return (
    <>
      <Navbar />
      <div className="admin-dashboard-page">
        <div className="admin-dashboard-header">
          <h2>Admin Dashboard</h2>
          <button className="logout-button" onClick={handleLogout}>Logout</button>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button 
            className={`tab-button ${activeTab === 'abstracts' ? 'active' : ''}`}
            onClick={() => setActiveTab('abstracts')}
          >
            Abstract Management ({abstracts.length})
          </button>
          <button 
            className={`tab-button ${activeTab === 'papers' ? 'active' : ''}`}
            onClick={() => setActiveTab('papers')}
          >
            Paper Management ({papers.length})
          </button>
          <button 
            className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            User Management
          </button>
        </div>

        {/* Abstracts Tab */}
        {activeTab === 'abstracts' && (
          <>
            {abstracts.length === 0 ? (
              <p className="no-data">No abstracts found.</p>
            ) : (
              <div className="table-wrapper">
                <table className="abstract-table">
                  <thead>
                    <tr>
                      <th>Abstract Code</th>
                      <th>Title</th>
                      <th>Author</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Remarks</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {abstracts.map((abstract, index) => (
                      <tr key={index}>
                        <td>{abstract.abstractCode}</td>
                        <td>{abstract.title}</td>
                        <td>{abstract.firstAuthorName || abstract.presentingAuthorName}</td>
                        <td>{abstract.email}</td>
                        <td className={`status ${abstract.status?.toLowerCase() || "pending"}`}>
                          {abstract.status || "Pending"}
                        </td>
                        <td>
                          <textarea
                            className="remarks-box"
                            placeholder="Add remarks (optional)"
                            value={remarks[abstract.abstractCode] || ""}
                            onChange={(e) =>
                              setRemarks({ ...remarks, [abstract.abstractCode]: e.target.value })
                            }
                            rows={2}
                          />
                        </td>
                        <td>
                          <button
                            className="approve-button"
                            onClick={() => updateStatus(abstract.uid, abstract.abstractCode, "Approved")}
                          >
                            Approve
                          </button>
                          <button
                            className="reject-button"
                            onClick={() => updateStatus(abstract.uid, abstract.abstractCode, "Rejected")}
                          >
                            Reject
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Papers Tab */}
        {activeTab === 'papers' && (
          <>
            {papers.length === 0 ? (
              <p className="no-data">No papers found.</p>
            ) : (
              <div className="table-wrapper">
                <table className="paper-table">
                  <thead>
                    <tr>
                      <th>Paper Code</th>
                      <th>Title</th>
                      <th>Abstract Code</th>
                      <th>Paper Type</th>
                      <th>Status</th>
                      <th>Submitted</th>
                      <th>Review</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {papers.map((paper, index) => (
                      <tr key={index}>
                        <td>{paper.paperCode}</td>
                        <td>{paper.title}</td>
                        <td>{paper.abstractCode}</td>
                        <td>{paper.paperType}</td>
                        <td className={`status ${paper.status?.toLowerCase().replace(/\s+/g, '-') || "pending"}`}>
                          {paper.status || "Pending"}
                        </td>
                        <td>{new Date(paper.submittedAt).toLocaleDateString()}</td>
                        <td>
                                                     {paper.review ? (
                             <div className="review-info">
                               {paper.review.overallScore && (
                                 <div><strong>Score:</strong> {paper.review.overallScore}/10</div>
                               )}
                               {paper.review.recommendation && (
                                 <div><strong>Recommendation:</strong> {paper.review.recommendation}</div>
                               )}
                               {paper.review.reviewerName && (
                                 <div><strong>Reviewer:</strong> {paper.review.reviewerName}</div>
                               )}
                               {paper.review.reviewerComments && (
                                 <div><strong>Comments:</strong> {paper.review.reviewerComments}</div>
                               )}
                             </div>
                           ) : (
                            <div className="review-form">
                              <textarea
                                className="review-comments"
                                placeholder="Reviewer comments..."
                                value={paperReviews[paper.paperCode]?.reviewerComments || ""}
                                onChange={(e) =>
                                  setPaperReviews({
                                    ...paperReviews,
                                    [paper.paperCode]: {
                                      ...paperReviews[paper.paperCode],
                                      reviewerComments: e.target.value
                                    }
                                  })
                                }
                                rows={3}
                              />
                              <div className="review-scores">
                                <input
                                  type="number"
                                  min="0"
                                  max="10"
                                  step="0.5"
                                  placeholder="Technical Score (0-10)"
                                  value={paperReviews[paper.paperCode]?.technicalScore || ""}
                                  onChange={(e) =>
                                    setPaperReviews({
                                      ...paperReviews,
                                      [paper.paperCode]: {
                                        ...paperReviews[paper.paperCode],
                                        technicalScore: e.target.value
                                      }
                                    })
                                  }
                                />
                                <input
                                  type="number"
                                  min="0"
                                  max="10"
                                  step="0.5"
                                  placeholder="Presentation Score (0-10)"
                                  value={paperReviews[paper.paperCode]?.presentationScore || ""}
                                  onChange={(e) =>
                                    setPaperReviews({
                                      ...paperReviews,
                                      [paper.paperCode]: {
                                        ...paperReviews[paper.paperCode],
                                        presentationScore: e.target.value
                                      }
                                    })
                                  }
                                />
                                <input
                                  type="number"
                                  min="0"
                                  max="10"
                                  step="0.5"
                                  placeholder="Overall Score (0-10)"
                                  value={paperReviews[paper.paperCode]?.overallScore || ""}
                                  onChange={(e) =>
                                    setPaperReviews({
                                      ...paperReviews,
                                      [paper.paperCode]: {
                                        ...paperReviews[paper.paperCode],
                                        overallScore: e.target.value
                                      }
                                    })
                                  }
                                />
                              </div>
                              <select
                                value={paperReviews[paper.paperCode]?.recommendation || ""}
                                onChange={(e) =>
                                  setPaperReviews({
                                    ...paperReviews,
                                    [paper.paperCode]: {
                                      ...paperReviews[paper.paperCode],
                                      recommendation: e.target.value
                                    }
                                  })
                                }
                              >
                                <option value="">Select Recommendation</option>
                                <option value="Accept">Accept</option>
                                <option value="Accept with Minor Revisions">Accept with Minor Revisions</option>
                                <option value="Major Revisions Required">Major Revisions Required</option>
                                <option value="Reject">Reject</option>
                              </select>
                              <input
                                type="text"
                                placeholder="Reviewer Name"
                                value={paperReviews[paper.paperCode]?.reviewerName || ""}
                                onChange={(e) =>
                                  setPaperReviews({
                                    ...paperReviews,
                                    [paper.paperCode]: {
                                      ...paperReviews[paper.paperCode],
                                      reviewerName: e.target.value
                                    }
                                  })
                                }
                              />
                            </div>
                          )}
                        </td>
                        <td>
                          {paper.paperFile && (
                            <a 
                              href={paper.paperFile} 
                              target="_blank" 
                              rel="noreferrer"
                              download
                              className="download-button"
                            >
                              Download Original
                            </a>
                          )}
                          {paper.updatedPaperFile && (
                            <a 
                              href={paper.updatedPaperFile} 
                              target="_blank" 
                              rel="noreferrer"
                              download
                              className="download-button updated"
                            >
                              Download Revised
                            </a>
                          )}
                          {paper.review ? (
                             <button
                               className="edit-review-button"
                               onClick={() => {
                                 // Populate review form with existing data
                                 setPaperReviews({
                                   ...paperReviews,
                                   [paper.paperCode]: {
                                     reviewerComments: paper.review.reviewerComments || "",
                                     technicalScore: paper.review.technicalScore || "",
                                     presentationScore: paper.review.presentationScore || "",
                                     overallScore: paper.review.overallScore || "",
                                     recommendation: paper.review.recommendation || "",
                                     reviewerName: paper.review.reviewerName || ""
                                   }
                                 });
                                 // Clear existing review to allow editing
                                 setPapers(prev =>
                                   prev.map(p =>
                                     p.paperCode === paper.paperCode 
                                       ? { ...p, review: null }
                                       : p
                                   )
                                 );
                               }}
                             >
                               Edit Review
                             </button>
                           ) : (
                                                         <button
                               className="submit-review-button"
                               onClick={() => updatePaperReview(paper.paperCode)}
                               disabled={!paperReviews[paper.paperCode] || Object.keys(paperReviews[paper.paperCode]).length === 0}
                             >
                               Submit Review
                             </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <>
            {users.length === 0 ? (
              <p className="no-data">No users found.</p>
            ) : (
              <div className="users-grid">
                {users.map((user, index) => (
                  <div key={index} className="user-card">
                    <div className="user-header">
                      <h3>{user.fullName}</h3>
                      <div className="user-header-right">
                        <span className="user-uid">{user.uid}</span>
                        <button 
                          className="login-as-user-btn"
                          onClick={() => handleMasterLogin(user)}
                          title="Login as this user"
                        >
                          🔑 Login as User
                        </button>
                      </div>
                    </div>
                    
                    <div className="user-info">
                      <div className="info-row">
                        <strong>Email:</strong> {user.email}
                      </div>
                      <div className="info-row">
                        <strong>Phone:</strong> {user.phone}
                      </div>
                      <div className="info-row">
                        <strong>Country:</strong> {user.country}
                      </div>
                      <div className="info-row">
                        <strong>Affiliation:</strong> {user.affiliation}
                      </div>
                      <div className="info-row">
                        <strong>Registration Date:</strong> {new Date(user.createdAt).toLocaleDateString()}
                      </div>
                    </div>

                    {/* Payments Section */}
                    <div className="user-section">
                      <h4>Payments ({user.payments?.length || 0})</h4>
                      {user.payments && user.payments.length > 0 ? (
                        <div className="payments-list">
                          {user.payments.map((payment, pIndex) => (
                            <div key={pIndex} className="payment-item">
                              <div className="payment-info">
                                <span className="payment-id">{payment.paymentId}</span>
                                <span className="payment-amount">{payment.currency} {payment.amount}</span>
                                <span className={`payment-status ${payment.status}`}>{payment.status}</span>
                              </div>
                              <div className="payment-date">
                                {new Date(payment.timestamp).toLocaleDateString()}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-payments">No payments recorded</p>
                      )}
                    </div>

                    {/* Abstract Submissions Section */}
                    <div className="user-section">
                      <h4>Abstract Submissions ({user.abstractSubmissions?.length || 0})</h4>
                      {user.abstractSubmissions && user.abstractSubmissions.length > 0 ? (
                        <div className="abstracts-list">
                          {user.abstractSubmissions.map((abstract, aIndex) => (
                            <div key={aIndex} className="abstract-item">
                              <div className="abstract-header">
                                <span className="abstract-code">{abstract.abstractCode}</span>
                                <span className={`abstract-status ${abstract.status?.toLowerCase() || 'pending'}`}>
                                  {abstract.status || 'Pending'}
                                </span>
                              </div>
                              <div className="abstract-title">{abstract.title}</div>
                              <div className="abstract-details">
                                <div className="abstract-field">
                                  <strong>Scope:</strong> {abstract.scope}
                                </div>
                                <div className="abstract-field">
                                  <strong>Presenting Type:</strong> {abstract.presentingType}
                                </div>
                                <div className="abstract-field">
                                  <strong>Abstract File:</strong>{" "}
                                  {abstract.abstractFile ? (
                                    <a
                                      href={abstract.abstractFile}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="download-button"
                                      style={{ marginLeft: '8px' }}
                                      download
                                    >
                                      Download Abstract
                                    </a>
                                  ) : (
                                    <span style={{ color: '#6c757d', marginLeft: '8px' }}>Not available</span>
                                  )}
                                </div>
                                <div className="abstract-field">
                                  <strong>First Author:</strong> {abstract.firstAuthorName} ({abstract.firstAuthorAffiliation})
                                </div>
                                {abstract.secondAuthorName && (
                                  <div className="abstract-field">
                                    <strong>Second Author:</strong> {abstract.secondAuthorName} ({abstract.secondAuthorAffiliation})
                                  </div>
                                )}
                                {abstract.otherAuthors && (
                                  <div className="abstract-field">
                                    <strong>Other Authors:</strong>
                                    <div className="authors-list">
                                      {(() => {
                                        try {
                                          // Try to parse as JSON array
                                          const authors = JSON.parse(abstract.otherAuthors);
                                          if (Array.isArray(authors)) {
                                            return authors.map((author, idx) => (
                                              <div key={idx} className="author-item">
                                                {author.name} ({author.affiliation})
                                              </div>
                                            ));
                                          }
                                        } catch (e) {
                                          // If parsing fails, treat as string
                                          return <div className="author-item">{abstract.otherAuthors}</div>;
                                        }
                                        return null;
                                      })()}
                                    </div>
                                  </div>
                                )}
                                <div className="abstract-field">
                                  <strong>Presenting Author:</strong> {abstract.presentingAuthorName} ({abstract.presentingAuthorAffiliation})
                                </div>
                                <div className="abstract-field">
                                  <strong>Finalized:</strong> {abstract.isFinalized ? 'Yes' : 'No'}
                                </div>
                                {abstract.remarks && (
                                  <div className="abstract-field">
                                    <strong>Remarks:</strong> {abstract.remarks}
                                  </div>
                                )}
                                {abstract.timestamp && (
                                  <div className="abstract-field">
                                    <strong>Submitted:</strong> {new Date(abstract.timestamp).toLocaleDateString()}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-abstracts">No abstract submissions</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Master Login Modal */}
      {showMasterLogin && selectedUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Master Login Required</h3>
              <button 
                className="close-modal-btn"
                onClick={() => {
                  setShowMasterLogin(false);
                  setSelectedUser(null);
                  setMasterPassword('');
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>You are about to login as:</p>
              <div className="selected-user-info">
                <strong>{selectedUser.fullName}</strong><br />
                <span className="user-email">{selectedUser.email}</span>
              </div>
              <div className="password-input-group">
                <label htmlFor="masterPassword">Enter Master Password:</label>
                <input
                  type="password"
                  id="masterPassword"
                  value={masterPassword}
                  onChange={(e) => setMasterPassword(e.target.value)}
                  placeholder="Enter master password"
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-btn"
                onClick={() => {
                  setShowMasterLogin(false);
                  setSelectedUser(null);
                  setMasterPassword('');
                }}
              >
                Cancel
              </button>
              <button 
                className="confirm-btn"
                onClick={handleMasterPasswordSubmit}
                disabled={!masterPassword.trim()}
              >
                Login as User
              </button>
            </div>
          </div>
        </div>
      )}

      <br />
      <br />
      <Footer />
    </>
  );
};

export default AdminDashboard;
